import { createFileRoute } from '@tanstack/react-router';
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsSection } from './-components/settings-section';
import { Switch } from '~/components/ui/switch';
import {
  browserPushSupported,
  hashBrowserEndpoint,
  registerPushWorker,
  serializePushSubscription,
  subscribeBrowser,
} from '~/notifications/browser';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/settings/notifications')({
  component: NotificationsSettingsPage,
});

type BrowserState =
  | { kind: 'loading' }
  | { kind: 'unsupported' }
  | {
      kind: 'ready';
      endpointHash: string | null;
      permission: NotificationPermission;
      subscription: PushSubscription | null;
    };

async function readBrowserState(): Promise<BrowserState> {
  if (!browserPushSupported()) {
    return { kind: 'unsupported' };
  }

  const registration = await registerPushWorker();
  const subscription = await registration.pushManager.getSubscription();

  return {
    kind: 'ready',
    permission: Notification.permission,
    subscription,
    endpointHash: subscription
      ? await hashBrowserEndpoint(subscription.endpoint)
      : null,
  };
}

function NotificationsSettingsPage() {
  const { t } = useTranslation('settings');
  const [browser, setBrowser] = useState<BrowserState>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { mutateAsync: enableSms } = useMutation(
    trpc.notifications.subscriptions.enableSms.mutationOptions(),
  );
  const { mutateAsync: disableSms } = useMutation(
    trpc.notifications.subscriptions.disableSms.mutationOptions(),
  );
  // Asked only once the browser has said which subscription it holds, since
  // the answer is about that subscription.
  const { data: status } = useQuery(
    trpc.notifications.subscriptions.status.queryOptions(
      browser.kind === 'loading'
        ? skipToken
        : {
            endpointHash:
              browser.kind === 'ready' ? browser.endpointHash : null,
          },
    ),
  );

  const inspectBrowser = useCallback(async () => {
    setBrowser(await readBrowserState());
  }, []);

  useEffect(() => {
    void readBrowserState().then(setBrowser, () =>
      setBrowser({ kind: 'unsupported' }),
    );
  }, []);

  async function changeEnabled(enabled: boolean) {
    if (browser.kind !== 'ready' || !status) {
      return;
    }

    setBusy(true);
    let createdSubscription: PushSubscription | null = null;

    try {
      if (enabled) {
        const permission =
          Notification.permission === 'granted'
            ? 'granted'
            : await Notification.requestPermission();

        if (permission !== 'granted' || !status.publicKey) {
          await inspectBrowser();
          return;
        }

        const { subscription, created } = await subscribeBrowser(
          status.publicKey,
        );
        createdSubscription = created ? subscription : null;

        await enableSms({
          subscription: serializePushSubscription(subscription),
        });
        notify.success(t('Browser notifications enabled.'));
      } else if (browser.subscription && browser.endpointHash) {
        await disableSms({ endpointHash: browser.endpointHash });
        await browser.subscription.unsubscribe();
        notify.success(t('Browser notifications disabled.'));
      }
    } catch {
      await createdSubscription?.unsubscribe().catch(() => false);
      notify.error(t('Unable to change browser notifications.'));
    } finally {
      await inspectBrowser().catch(() => null);
      await queryClient.invalidateQueries({
        queryKey: trpc.notifications.subscriptions.status.queryKey(),
      });
      setBusy(false);
    }
  }

  const permissionDenied =
    browser.kind === 'ready' && browser.permission === 'denied';
  const unavailable = status !== undefined && !status.available;
  const disabled =
    busy ||
    status === undefined ||
    unavailable ||
    browser.kind !== 'ready' ||
    permissionDenied;

  return (
    <SettingsSection.Wrapper>
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Notifications')}</SettingsSection.Title>
        <SettingsSection.Description>
          {t(
            'Choose whether this browser shows new SMS messages during office hours.',
          )}
        </SettingsSection.Description>
      </SettingsSection.Header>

      <SettingsSection.ContentWrapper className="flex items-start justify-between gap-6">
        <div className="space-y-1">
          <label
            htmlFor="sms-browser-notifications"
            className="text-sm font-medium text-foreground"
          >
            {t('New SMS messages in this browser')}
          </label>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t(
              'They can appear after you close the MintyAssistant tab. The sender and message text may be visible on a lock screen or shared computer. Each browser must be enabled separately.',
            )}
          </p>
          {permissionDenied && (
            <p className="text-sm text-destructive">
              {t(
                'Notifications are blocked. Allow them in your browser settings first.',
              )}
            </p>
          )}
          {browser.kind === 'unsupported' && (
            <p className="text-sm text-muted-foreground">
              {t(
                'This browser does not support Web Push here. On iPhone or iPad, add MintyAssistant to the Home Screen first.',
              )}
            </p>
          )}
          {unavailable && (
            <p className="text-sm text-muted-foreground">
              {t('Browser notifications are not enabled for this clinic.')}
            </p>
          )}
        </div>

        <div className="flex min-h-8 items-center">
          {busy && <Loader2 className="mr-3 size-4 animate-spin" />}
          <Switch
            id="sms-browser-notifications"
            checked={status?.enabled ?? false}
            disabled={disabled}
            onCheckedChange={(checked) => void changeEnabled(checked)}
            aria-label={t('New SMS messages in this browser')}
          />
        </div>
      </SettingsSection.ContentWrapper>
    </SettingsSection.Wrapper>
  );
}
